import React, { useMemo, useState } from 'react';
import { PackageIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Material } from '../../types/pro';
import { kr } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProMaterialsProps {
  materials: Material[];
  onAdd: (material: Omit<Material, 'id'>) => void;
  onUpdate: (id: string, changes: Partial<Material>) => void;
  onRemove: (id: string) => void;
}
const emptyDraft = {
  name: '',
  itemNo: '',
  unit: 'stk',
  costPrice: '',
  salesPrice: '',
  category: 'Diverse'
};
export function ProMaterials({
  materials,
  onAdd,
  onUpdate,
  onRemove
}: ProMaterialsProps) {
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return materials.filter(material => term ? [material.name, material.itemNo, material.category].join(' ').toLowerCase().includes(term) : true).sort((a, b) => a.name.localeCompare(b.name, 'da'));
  }, [materials, query]);
  const margin = (material: Material) => {
    const kroner = material.salesPrice - material.costPrice;
    const percent = material.salesPrice > 0 ? kroner / material.salesPrice * 100 : 0;
    return {
      kroner,
      percent
    };
  };
  const submit = () => {
    if (!draft.name.trim()) return;
    onAdd({
      name: draft.name.trim(),
      itemNo: draft.itemNo.trim() || '—',
      unit: draft.unit.trim() || 'stk',
      stock: 0,
      costPrice: parseFloat(draft.costPrice) || 0,
      salesPrice: parseFloat(draft.salesPrice) || 0,
      category: draft.category.trim() || 'Diverse'
    });
    setDraft(emptyDraft);
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm tabular-nums outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500';
  return <div data-id="element-1735" data-mp="yff5f38zg422">
      <PageHeader title="Materialer" subtitle={`${materials.length} varer i kartoteket — bruges både på opgaver og i tilbud`} data-id="element-1736" data-mp="yf6tbdkop2rx" />

      <SearchField value={query} onChange={setQuery} placeholder="Søg materiale eller varenummer…" className="mb-4 max-w-md" data-id="element-1737" data-mp="1m5sxuuvhc4x6" />

      {rows.length === 0 ? <EmptyState icon={PackageIcon} title="Ingen materialer matcher" data-id="element-1738" data-mp="1ceogxpm8u9zp" /> : <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-1739" data-mp="1hczr61ggamig">
          <table className="w-full min-w-[720px] text-sm" data-id="element-1740" data-mp="252u3mhwuy926">
            <thead data-id="element-1741" data-mp="29ulms7dopcia">
              <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-1742" data-mp="29v3srvq5f53e">
                {['Materiale', 'Varenummer', 'Enhed', 'Indkøbspris', 'Salgspris', 'Avance'].map(heading => <th key={heading} scope="col" className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1743" data-mp="76vhhzcgvqgh">
                    {heading}
                  </th>)}
                <th className="w-10" data-id="element-1744" data-mp="36ntpgvga7fr2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-1745" data-mp="3kml027vxqjag">
              {rows.map(material => {
            const {
              kroner,
              percent
            } = margin(material);
            const thin = percent < 25;
            return <tr key={material.id} data-id="element-1746" data-mp="zvkjs1p9e1tt">
                    <td className="px-3 py-1.5" data-id="element-1747" data-mp="zyot1dhkznrc">
                      <input value={material.name} onChange={e => material.id && onUpdate(material.id, {
                  name: e.target.value
                })} aria-label="Materialenavn" className={`${cell} font-medium text-stone-900`} data-id="element-1748" data-mp="uyak7t85o9s9" />
                      <span className="block px-2 text-xs text-stone-400" data-id="element-1749" data-mp="255zrh1qgsl2n">
                        {material.category}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-stone-500" data-id="element-1750" data-mp="2e5d5m7pj9xtp">
                      {material.itemNo}
                    </td>
                    <td className="px-3 py-1.5 text-stone-600" data-id="element-1751" data-mp="2iwv66unn9we5">
                      {material.unit}
                    </td>
                    <td className="w-28 px-3 py-1.5" data-id="element-1752" data-mp="2nxagtegjkpsf">
                      <input type="number" value={material.costPrice} onChange={e => material.id && onUpdate(material.id, {
                  costPrice: parseFloat(e.target.value) || 0
                })} aria-label="Indkøbspris" className={`${cell} text-right`} data-id="element-1753" data-mp="2svkbzp29nqe1" />
                    </td>
                    <td className="w-28 px-3 py-1.5" data-id="element-1754" data-mp="7auetrwf362s">
                      <input type="number" value={material.salesPrice} onChange={e => material.id && onUpdate(material.id, {
                  salesPrice: parseFloat(e.target.value) || 0
                })} aria-label="Salgspris" className={`${cell} text-right font-medium`} data-id="element-1755" data-mp="14x1huxd2oqd1" />
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right" data-id="element-1756" data-mp="1iv82vcwxavva">
                      <span className={`block text-sm font-semibold tabular-nums ${thin ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-1757" data-mp="3d5ohwqbsjnhg">
                        {kr(kroner)}
                      </span>
                      <span className={`block text-xs tabular-nums ${thin ? 'text-amber-500' : 'text-stone-400'}`} data-id="element-1758" data-mp="38cdfr2hxbuvv">
                        {percent.toFixed(0)} %
                      </span>
                    </td>
                    <td className="px-1" data-id="element-1759" data-mp="2khk7xsm6so8y">
                      <button onClick={() => material.id && onRemove(material.id)} aria-label={`Slet ${material.name}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1760" data-mp="2peiy602qcb1v">
                        <Trash2Icon className="h-4 w-4" data-id="element-1761" data-mp="xql0e6kp79v2" />
                      </button>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}

      <section className="mt-5 rounded-xl border border-stone-200 bg-white p-5" data-id="element-1762" data-mp="16khj1benm45q">
        <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1763" data-mp="11sptn478i4h6">
          Tilføj materiale
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6" data-id="element-1764" data-mp="1ggsujz6f6pz4">
          <input value={draft.name} onChange={e => setDraft({
          ...draft,
          name: e.target.value
        })} placeholder="Materiale" aria-label="Materiale" className={`${field} col-span-2`} data-id="element-1765" data-mp="1bj15dcx5thym" />
          <input value={draft.itemNo} onChange={e => setDraft({
          ...draft,
          itemNo: e.target.value
        })} placeholder="Varenummer" aria-label="Varenummer" className={field} data-id="element-1766" data-mp="1lhgq8y5lfuzo" />
          <input value={draft.unit} onChange={e => setDraft({
          ...draft,
          unit: e.target.value
        })} placeholder="Enhed" aria-label="Enhed" className={field} data-id="element-1767" data-mp="fo458nknkyr6" />
          <input type="number" value={draft.costPrice} onChange={e => setDraft({
          ...draft,
          costPrice: e.target.value
        })} placeholder="Indkøb" aria-label="Indkøbspris" className={field} data-id="element-1768" data-mp="peqf015ouv1y" />
          <input type="number" value={draft.salesPrice} onChange={e => setDraft({
          ...draft,
          salesPrice: e.target.value
        })} placeholder="Salg" aria-label="Salgspris" className={field} data-id="element-1769" data-mp="1ntctg3p3c3w" />
        </div>
        <button onClick={submit} disabled={!draft.name.trim()} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-1770" data-mp="ameu77xfkl33">
          <PlusIcon className="h-4 w-4" data-id="element-1771" data-mp="1r8s3ju7vf71e" />
          Tilføj til kartotek
        </button>
      </section>
    </div>;
}